// YouTube RSS fallback service
export interface RSSVideo {
  id: string;
  title: string;
  thumbnail: string;
  channelName: string;
  channelId: string;
  published: string;
  publishedDate: string;
}

export const youtubeRssService = {
  // Get videos from a channel's RSS feed with multiple proxy fallbacks
  getChannelRSSVideos: async (channelId: string): Promise<RSSVideo[]> => {
    const proxyServices = [
      { name: 'ThingProxy', url: `https://thingproxy.freeboard.io/fetch/`, type: 'direct' },
      { name: 'AllOrigins', url: `https://api.allorigins.win/get?url=`, type: 'json' },
      { name: 'Proxy-CORS', url: `https://proxy-cors.herokuapp.com/`, type: 'direct' },
      { name: 'CORS-Fix', url: `https://cors-fix.herokuapp.com/`, type: 'direct' }
    ];
    
    for (let i = 0; i < proxyServices.length; i++) {
      try {
        const proxy = proxyServices[i];
        const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;
        const proxyUrl = proxy.type === 'direct' 
          ? `${proxy.url}${rssUrl}`
          : `${proxy.url}${encodeURIComponent(rssUrl)}`;
        
        console.log(`🔄 Fetching RSS for channel ${channelId} (${proxy.name})...`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout
        
        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          console.warn(`⚠️ Proxy ${proxy.name} returned HTTP ${response.status} for ${channelId}, trying next...`);
          continue;
        }
        
        let xmlText: string;
        
        if (proxy.type === 'json') {
          const data = await response.json();
          if (!data.contents) {
            console.warn(`⚠️ Empty content from ${proxy.name} for ${channelId}, trying next...`);
            continue;
          }
          xmlText = data.contents;
        } else {
          xmlText = await response.text();
        }
        
        // Parse XML using DOMParser
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
        
        // Check for XML parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          console.warn(`⚠️ XML parsing failed with ${proxy.name} for ${channelId}, trying next...`);
          continue;
        }
        
        const entries = xmlDoc.querySelectorAll('entry');
        const videos: RSSVideo[] = [];
        
        entries.forEach(entry => {
          const videoId = entry.querySelector('yt\\:videoId')?.textContent || 
                         entry.querySelector('videoId')?.textContent || '';
          const title = entry.querySelector('title')?.textContent || '';
          const channelName = entry.querySelector('author name')?.textContent ||
                             entry.querySelector('name')?.textContent || '';
          const published = entry.querySelector('published')?.textContent || '';
          
          // Extract thumbnail
          let thumbnail = entry.querySelector('media\\:thumbnail')?.getAttribute('url') || '';
          if (!thumbnail && videoId) {
            thumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
          }
          
          if (videoId && title) {
            const publishedDate = new Date(published);
            const now = new Date();
            const diffTime = Math.abs(now.getTime() - publishedDate.getTime());
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            
            let publishedText = '';
            if (diffDays < 1) {
              const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
              if (diffHours < 1) {
                const diffMinutes = Math.floor(diffTime / (1000 * 60));
                publishedText = `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
              } else {
                publishedText = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
              }
            } else if (diffDays === 1) {
              publishedText = '1 day ago';
            } else if (diffDays < 7) {
              publishedText = `${diffDays} days ago`;
            } else if (diffDays < 30) {
              const diffWeeks = Math.floor(diffDays / 7);
              publishedText = `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
            } else {
              const diffMonths = Math.floor(diffDays / 30);
              publishedText = `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
            }
            
            videos.push({
              id: videoId,
              title,
              thumbnail,
              channelName,
              channelId,
              published: publishedText,
              publishedDate: new Date(published).toISOString() // Garder la date ISO complète
            });
          }
        });
        
        console.log(`✅ Found ${videos.length} videos for channel ${channelId} via ${proxy.name}`);
        return videos; // Return as soon as we get successful results
        
      } catch (error) {
        const proxy = proxyServices[i];
        // Only log warning, not error, as this is expected behavior
        console.warn(`⚠️ Proxy ${proxy.name} failed for channel ${channelId}, trying next...`);
        
        // If this is the last proxy, log and return empty array
        if (i === proxyServices.length - 1) {
          console.warn(`⚠️ All proxies exhausted for channel ${channelId}, skipping...`);
          return [];
        }
        // Otherwise, continue to next proxy
      }
    }
    
    return []; // Fallback if no proxy worked
  },

  // Get videos from multiple channels using RSS with better error handling
  getMultipleChannelsRSSVideos: async (channelIds: string[]): Promise<RSSVideo[]> => {
    try {
      console.log(`📡 Fetching RSS feeds for ${channelIds.length} channels...`);
      
      // Process channels in smaller batches to avoid overwhelming proxy services
      const batchSize = 2; // Reduced batch size for better reliability
      const allVideos: RSSVideo[] = [];
      
      for (let i = 0; i < channelIds.length; i += batchSize) {
        const batch = channelIds.slice(i, i + batchSize);
        console.log(`📡 Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(channelIds.length / batchSize)} (${batch.length} channels)`);
        
        const promises = batch.map(async (channelId) => {
          try {
            const videos = await youtubeRssService.getChannelRSSVideos(channelId);
            if (videos.length > 0) {
              console.log(`✅ Channel ${channelId}: ${videos.length} videos`);
            } else {
              console.warn(`⚠️ No videos found for channel ${channelId}`);
            }
            return videos;
          } catch (error) {
            console.warn(`⚠️ Skipping channel ${channelId} due to error`);
            return [];
          }
        });
        
        const batchResults = await Promise.allSettled(promises);
        
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            allVideos.push(...result.value);
          } else {
            console.warn(`⚠️ Channel ${batch[index]} skipped due to fetch failure`);
          }
        });
        
        // Add delay between batches to be respectful to proxy services
        if (i + batchSize < channelIds.length) {
          await new Promise(resolve => setTimeout(resolve, 1500)); // Increased to 1.5 seconds
        }
      }
      
      // Sort by published date (newest first)
      const sortedVideos = allVideos.sort((a, b) => 
        new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
      );
      
      console.log(`✅ RSS fetch complete: ${sortedVideos.length} total videos from ${channelIds.length} channels`);
      console.log(`📊 Videos per channel average: ${Math.round(sortedVideos.length / channelIds.length)}`);
      
      return sortedVideos;
    } catch (error) {
      console.error('❌ Error fetching multiple RSS feeds:', error);
      return [];
    }
  }
};
